import Big from 'big.js';
import _ from 'lodash';

import * as graphql from 'generated/graphql';
import { ITypeSpecificPolicy } from './shared';



export const GlAccount: ITypeSpecificPolicy<graphql.GlAccount> = {
  fields: {

    /**
     * For the set of data points, this gets the most recent year, then the most recent period, the
     * periods are sorted as:
     *
     * fy
     * q1
     * q2
     * q3
     * q4
     *
     * The last period in the list would be chosen.
     */
    mostRecentValue: {
      read: (existing, { readField }) => {
        const values = readField<graphql.AccountDataPoint[]>('values');
        if (!values) {
          return existing;
        }
        const year2Rows = _.groupBy(values, (v) => v.year);

        const latestYear = _.last(_.sortBy(_.keys(year2Rows)));
        if (!latestYear) {
          return existing;
        }

        const latestYearValues = year2Rows[+latestYear];
        const latestDataPoint = _.last(_.sortBy(latestYearValues, (v) => v.period));
        return latestDataPoint;
      }
    },


    chatDisplayName: {
      read: (existing, { readField }) => {
        const fieldValue = readField<ModifiedGLAccountType>('name');
        if (!fieldValue) {
          return existing;
        }

        if (!_.has(displayName2Shortened, fieldValue)) {
          return fieldValue;
        }

        return displayName2Shortened[fieldValue];
      }
    },

    contextCategory: {
      read: (existing, { readField }) => {
        const fieldValue = readField<ModifiedGLAccountType>('name');
        if (!fieldValue) {
          return existing;
        }

        if (!_.has(displayName2ContextCategory, fieldValue)) {
          return fieldValue;
        }

        return displayName2ContextCategory[fieldValue];
      }
    },

    fiveYearCompoundAnnualGrowthRate: {
      read: (existing, { readField }) => {
        const fyValues = readField<graphql.AccountDataPoint[]>('fiscalYearValues');
        if (!fyValues) {
          return existing;
        }

        const mostRecent = _.first(fyValues);
        if (!mostRecent) {
          return existing;
        }

        const oldest = __getMostRelevantBeginningForCagr(fyValues, mostRecent.year);
        if (!oldest) {
          return existing;
        }

        const ending = Big(mostRecent.value);
        const beginning = Big(oldest.value);

        if (beginning.eq(0)) {
          return existing;
        }

        const val = ending.div(beginning).toNumber() ** (1 / (mostRecent.year - oldest.year));

        if (_.isNaN(val)) {
          return existing;
        }

        if (val === Infinity) {
          return existing;
        }

        return Big(val)
          .minus(1)
          .mul(100);
      }
    },

    fiveYearAverage: {
      read: (existing, { readField }) => {
        const fyValues = readField<graphql.AccountDataPoint[]>('fiscalYearValues');
        if (!fyValues || _.isEmpty(fyValues)) {
          return existing;
        }

        const last5Years = fyValues.slice(0, 5);

        if (last5Years.length === 0) {
          return existing;
        }

        const theSum = last5Years.reduce((sum, fyValue) => {
          return sum.plus(Big(fyValue.value));
        }, Big(0));

        return theSum.div(last5Years.length);
      }
    },

    formattedFiveYearAverage: {
      read: (existing, { readField }) => {
        const avg = readField<Big>('fiveYearAverage');
        const values = readField<graphql.AccountDataPoint[]>('values');
        if (values && !_.isEmpty(values)) {
          const unit = values[0].unit;

          if (unit === 'percentage') {
            return avg?.mul(100).toFixed(2) + ' %';
          }
        }

        return avg?.toFixed(1);
      }
    },



    fiscalYearValues: {
      read: (existing, { readField }) => {
        const values = readField<graphql.AccountDataPoint[]>('values');
        if (!values) {
          return existing;
        }

        return values.filter((v) => v.period === 'fy').toSorted((a, b) => a.year - b.year)
          .toReversed();
      }
    },



    financialsDisplayName: {
      read: (existing, { readField }) => {
        const fieldValue = readField<graphql.GlAccountTypeName>('name');
        if (!fieldValue) {
          return existing;
        }

        if (!_.has(name2FinancialDisplayName, fieldValue)) {
          return readField('displayName');
        }

        return name2FinancialDisplayName[fieldValue];
      }
    },



    yearsContainingData: {
      read: (existing, { readField }) => {
        const vals = readField<graphql.AccountDataPoint[]>('fiscalYearValues');
        const years = vals?.map((v) => v.year) ?? [];
        return _.uniq(years);
      }
    },

    definition: {
      read: (existing, { readField }) => {
        const name = readField<graphql.GlAccountTypeName>('name');
        if (!name) {
          return '';
        }

        if (!_.has(name2Definition, name)) {
          return '';
        }

        return name2Definition[name];
      }
    },

    formula: {
      read: (existing, { readField }) => {
        const name = readField<graphql.GlAccountTypeName>('name');
        if (!name) {
          return null;
        }

        if (!_.has(name2Formula, name)) {
          return null;
        }

        return name2Formula[name];
      }
    }
  }
};



/**
 * Gets the beginning value for a CAGR calculation. Sherlock does a 5 year CAGR. However, the
 * dataset is sometimes incomplete, and a datapoint from 5 years ago isn't always able to be found.
 *
 * @param values an array of values sorted by year.
 * @param newestYear the year to anchor against
 * @returns the data point from 5 years ago, if not found, the oldest.
 */
function __getMostRelevantBeginningForCagr(
  values: readonly graphql.AccountDataPoint[],
  newestYear: number
) {
  const fiveYearsAgo = newestYear - 5;
  const val = values.find((v) => v.year === fiveYearsAgo);
  return val ?? _.last(values) ?? null;
}


const name2FinancialDisplayName: Partial<Record<graphql.GlAccountTypeName, string>> = {
  dividendyield: 'Div Yld (%)',
  totalrevenue: 'Sales',
  ebitda: 'EBITDA',
  ebit: 'EBIT',
  netincome: 'Net Inc',
  dilutedeps: 'EPS (Dil)',
  cashdividendspershare: 'Divs PS',
  adjweightedavedilutedsharesos: 'Shs Out (Dil)',
  cashandequivalents: 'Cash & ST Inv',
  totalassets: 'Assets',
  networkingcapital: 'Wkg Cap',
  longtermdebt: 'LT Debt',
  netcashfromoperatingactivities: 'Net Op CF',
  capex: 'Cap Ex',
  freecashflow: 'FCF',
  grossmargin: 'Gross Margin (%)',
  ebitdamargin: 'EBITDA Margin (%)',
  ebitmargin: 'EBIT Margin (%)',
  profitmargin: 'Net Margin (%)',
  roa: 'ROA (%)',
  roe: 'ROE (%)',
  assetturnover: 'Asset Turn',
  dso: 'DSO',
  dpo: 'DPO',
  debttototalcapital: 'TDebt%Cap',
  debttoequity: 'TDebt%TEQ',
  bookvaluepershare: 'Bk PS'
};


const displayName2Shortened: Record<ModifiedGLAccountType, string> = {
  currentratio: 'Current Ratio',
  altmanzscore: 'Altman Z-Score',
  assetturnover: 'Asset Turnover',
  bookvaluepershare: 'Book Value Per Share',
  capex: 'CapEx',
  croic: 'CROIC',
  debt: 'Total Debt',
  debttoebitda: 'Debt to EBITDA',
  debttoequity: 'Debt to Equity',
  debttonopat: 'Debt to NOPAT',
  debttototalcapital: 'Debt to Capital',
  dillutedearningspershare: 'EPS (Diluted)',
  dso: 'Days Inventory Outstanding',
  dividendyield: 'Dividend Yield',
  divpayoutratio: 'Dividend Payout Ratio',
  ebit: 'EBIT',
  ebitda: 'EBITDA',
  ebitdagrowth: 'EBITDA Growth',
  ebitdamargin: 'EBITDA Margin',
  ebitgrowth: 'EBIT Growth',
  ebitmargin: 'EBIT Margin',
  efftaxrate: 'Effective Tax Rate',
  enterprisevalue: 'Enterprise Value',
  enterprisevaluetoebitda: 'EV/EVBITDA',
  enterprisevaluetoebit: 'EV/EBIT',
  enterprisevaluetoocf: 'EV/OCF',
  epsgrowth: 'EPS Growth',
  fcffgrowth: 'FCF Growth',
  goodwill: 'Goodwill',
  grossmargin: 'Gross Margin',
  interestburdenpct: 'Interest Burden Percent',
  longtermdebt: 'Total Long-term Debt',
  ltdebttoebitda: 'LT Debt to EBITDA',
  ltdebttoequity: 'LT Debt to Equity',
  ltdebttonopat: 'LT Debt to NOPAT',
  ltdebttocap: 'LT Debt to Capital',
  marketcap: 'Market Cap',
  netincome: 'Consolidated Net Income',
  netincomegrowth: 'Net Income Growth',
  netinventory: 'Inventories, Net',
  nopat: 'NOPAT',
  nopatgrowth: 'NOPAT Growth',
  nopatmargin: 'NOPAT Margin',
  operatingmargin: 'Op Margin',
  paymentofdividends: 'Payment of Dividends',
  pricetobook: 'P/BV',
  pricetoearnings: 'P/E',
  pricetorevenue: 'P/Rev',
  profitmargin: 'Net Income Margin',
  quickratio: 'Quick Ratio',
  repurchaseofcommonequity: 'Share Repurchases',
  roe: 'ROE',
  revenuegrowth: 'Revenue Growth',
  roa: 'ROA',
  roic: 'ROIC',
  tangbookvaluepershare: 'Tangible BV per Share',
  totalrevenue: 'Total Revenue'
};

const displayName2ContextCategory: Record<ModifiedGLAccountType, graphql.CompanyContextCategoryName> = {
  currentratio: 'liquidity',
  altmanzscore: 'liquidity',
  assetturnover: 'costStructureAndEfficiency',
  bookvaluepershare: 'valuation',
  capex: 'costStructureAndEfficiency',
  croic: 'valuation',
  debt: 'costStructureAndEfficiency',
  debttoebitda: 'costStructureAndEfficiency',
  debttoequity: 'costStructureAndEfficiency',
  debttonopat: 'costStructureAndEfficiency',
  debttototalcapital: 'costStructureAndEfficiency',
  dillutedearningspershare: 'earnings',
  dso: 'costStructureAndEfficiency',
  dividendyield: 'dividends',
  divpayoutratio: 'dividends',
  ebit: 'earnings',
  ebitda: 'earnings',
  ebitdagrowth: 'earnings',
  ebitdamargin: 'margins',
  ebitgrowth: 'earnings',
  ebitmargin: 'margins',
  efftaxrate: 'costStructureAndEfficiency',
  enterprisevalue: 'valuation',
  enterprisevaluetoebitda: 'valuation',
  enterprisevaluetoebit: 'valuation',
  enterprisevaluetoocf: 'valuation',
  epsgrowth: 'earnings',
  fcffgrowth: 'earnings',
  goodwill: 'costStructureAndEfficiency',
  grossmargin: 'margins',
  interestburdenpct: 'costStructureAndEfficiency',
  longtermdebt: 'costStructureAndEfficiency',
  ltdebttoebitda: 'costStructureAndEfficiency',
  ltdebttoequity: 'costStructureAndEfficiency',
  ltdebttonopat: 'costStructureAndEfficiency',
  ltdebttocap: 'costStructureAndEfficiency',
  marketcap: 'valuation',
  netincome: 'earnings',
  netincomegrowth: 'earnings',
  netinventory: 'costStructureAndEfficiency',
  nopat: 'earnings',
  nopatgrowth: 'earnings',
  nopatmargin: 'margins',
  operatingmargin: 'margins',
  paymentofdividends: 'dividends',
  pricetobook: 'valuation',
  pricetoearnings: 'valuation',
  pricetorevenue: 'valuation',
  profitmargin: 'margins',
  quickratio: 'costStructureAndEfficiency',
  repurchaseofcommonequity: 'dividends',
  roe: 'costStructureAndEfficiency',
  revenuegrowth: 'revenue',
  roa: 'costStructureAndEfficiency',
  roic: 'costStructureAndEfficiency',
  tangbookvaluepershare: 'valuation',
  totalrevenue: 'revenue'
};

const name2Definition: Partial<Record<graphql.GlAccountTypeName, string>> = {
  dividendyield: 'A financial ratio that indicates how much a company pays out in dividends each year relative to its share price. Dividend yield is represented as a percentage and can be calculated by dividing the dollar value of dividends paid in a given year per share of stock held by the dollar value of one share of stock.',
  totalrevenue: 'Revenue is the amount of money that a company actually receives during a specific period, including discounts and deductions for returned merchandise. It is the "top line" or "gross income" figure from which costs are subtracted to determine net income.',
  ebitda: 'EBITDA stands for earnings before interest, taxes, depreciation and amortization. EBITDA is one indicator of a company\'s financial performance and is used as a proxy for the earning potential of a business, although doing so has its drawbacks. Further, EBITDA strips out the cost of debt capital and its tax effects by adding back interest and taxes to earnings.',
  ebit: 'EBIT measures the profit a company generates from its operations, making it synonymous with "operating profit." By ignoring tax and interest expenses, it focuses solely on a company\'s ability to generate earnings from operations, ignoring variables such as the tax burden and capital structure. This focus makes EBIT an especially useful metric for certain applications.',
  netincome: 'Net income (NI) is a company\'s total earnings (or profit); net income is calculated by taking revenues and subtracting the costs of doing business such as depreciation, interest, taxes and other expenses. This number appears on a company\'s income statement and is an important measure of how profitable the company is over a period of time.',
  dilutedeps: 'Diluted EPS is a performance metric used to gauge the quality of a company\'s earnings per share (EPS) if all convertible securities were exercised. Convertible securities are all outstanding convertible preferred shares, convertible debentures, stock options (primarily employee-based) and warrants.',
  adjweightedavedilutedsharesos: 'The weighted average of diluted outstanding shares is a calculation that incorporates any changes in the amount of outstanding shares over a reporting period. Diluted shares outstanding includes all shares that would be created upon conversion into shares. Convertible securities are all outstanding convertible preferred shares, convertible debentures, stock options (primarily employee-based) and warrants. Adjusted for stock splits.',
  cashandequivalents: 'Cash and cash equivalents refer to the line item on the balance sheet that reports the value of a company\'s assets that are cash or can be converted into cash immediately. These include bank accounts, marketable securities, commercial paper, Treasury bills and short-term government bonds with a maturity date of three months or less. Marketable securities and money market holdings are considered cash equivalents because they are liquid and not subject to material fluctuations in value.',
  totalassets: 'Total assets are the sum of all current and noncurrent assets that a company owns. They are reported on the company balance sheet. The total asset figure is based on the purchase price of the listed assets, and not the fair market value.',
  netcashfromoperatingactivities: 'Cash flow from operating activities (CFO) is an accounting item indicating the money a company brings in from ongoing, regular business activities, such as manufacturing and selling goods or providing a service. Cash flow from operating activities does not include long-term capital or investment costs. It does include earnings before interest and taxes plus depreciation minus taxes.',
  freecashflow: 'Free cash flow for the firm (FCFF) is a measure of financial performance that expresses the net amount of cash that is generated for a firm after expenses, taxes and changes in net working capital and investments are deducted. FCFF is essentially a measurement of a company\'s profitability after all expenses and reinvestments. It\'s one of the many benchmarks used to compare and analyze financial health.',
  grossmargin: 'The gross margin represents the percent of total sales revenue that the company retains after incurring the direct costs associated with producing the goods and services it sells. The higher the percentage, the more the company retains on each dollar of sales, to service its other costs and debt obligations.',
  ebitdamargin: 'EBITDA margin is a measurement of a company\'s operating profitability as a percentage of its total revenue. EBITDA margin can provide an investor, business owner or financial professional with a clear view of a company\'s operating profitability and cash flow.',
  ebitmargin: 'EBIT Margin is the ratio of Earnings before Interest and Taxes to net revenue - earned. It is a measure of a company\'s profitability on sales over a specific time period. Interpretation- This indicator gives information on a company\'s earnings ability.',
  profitmargin: 'Profit margin is part of a category of profitability ratios and it measures how much out of every dollar of sales a company actually keeps in earnings.',
  roa: 'Return on assets (ROA) is an indicator of how profitable a company is relative to its total assets. ROA gives an idea as to how efficient management is at using its assets to generate earnings. ROA tells you what earnings were generated from invested capital (assets). ROA for public companies can vary substantially and will be highly dependent on the industry. This is why when using ROA as a comparative measure, it is best to compare it against a company\'s previous ROA numbers or the ROA of a similar company.',
  roe: 'Return on equity (ROE) is the amount of net income returned as a percentage of shareholders equity. Return on equity measures a corporation\'s profitability by revealing how much profit a company generates with the money shareholders have invested. The ROE is useful for comparing the profitability of a company to that of other firms in the same industry.',
  assetturnover: 'Asset turnover is the value of a companys sales or revenues generated relative to the value of its assets. The Asset Turnover can often be used as an indicator of the efficiency with which a company is deploying its assets in generating revenue. In general, the higher the asset turnover ratio, the better the company is performing, since higher ratios imply that the company is generating more revenue per dollar of assets.',
  dso: 'Days sales outstanding (DSO) is a measure of the average number of days that a company takes to collect revenue after a sale has been made. DSO is often determined on a monthly, quarterly or annual basis and can be calculated by dividing the amount of accounts receivable during a given period by the total value of credit sales during the same period, and multiplying the result by the number of days in the period measured.',
  dpo: 'Days payable outstanding (DPO) is a company\'s average payable period. Days payable outstanding tells how long it takes a company to pay its invoices from trade creditors, such as suppliers. DPO is typically looked at either quarterly or yearly.',
  currentratio: 'The current ratio is a liquidity ratio that measures a company\'s ability to pay short-term and long-term obligations. The current ratio is mainly used to give an idea of the company\'s ability to pay back its liabilities (debt and accounts payable) with its assets (cash, marketable securities, inventory, accounts receivable). As such, current ratio can be used to take a rough measurement of a companys financial health. The higher the current ratio, the more capable the company is of paying its obligations, as it has a larger proportion of asset value relative to the value of its liabilities.',
  quickratio: 'The quick ratio is an indicator of a companys short-term liquidity. The quick ratio measures a companys ability to meet its short-term obligations with its most liquid assets. The quick ratio is more conservative than the current ratio because it excludes inventories from current assets. The ratio derives its name presumably from the fact that assets such as cash and marketable securities are quick sources of cash.'
};

const name2Formula: Partial<Record<graphql.GlAccountTypeName, string>> = {
  dividendyield: 'Dividend Yield=\\frac{Cash Dividends Per Share}{Close Price}',
  ebitda: 'Ebitda=Ebit+Depreciation and Amortization',
  ebit: 'Ebit=Operating Income+Other Nonoperating Income',
  freecashflow: 'Free Cash Flow=Nopat-Invested Capital Increase Decrease',
  grossmargin: 'Gross Margin=\\frac{Total Gross Profit}{Total Revenue}',
  ebitdamargin: 'Ebitda Margin=\\frac{Ebitda}{Total Revenue}',
  ebitmargin: 'Ebit Margin=\\frac{Ebit}{Total Revenue}',
  profitmargin: 'Profit Margin=\\frac{Net Income}{Total Revenue}',
  roa: 'ROA=Profit Margin\\times{Asset Turnover}',
  roe: 'ROE=ROIC+RNNOA',
  assetturnover: 'Asset Turnover=\\frac{Total Revenue}{Total Assets}',
  dso: 'DSO=\\frac{365}{AR Turnover}',
  dpo: 'DPO=\\frac{365}{AP Turnover}',
  currentratio: 'Current Ratio=\\frac{Total Current Assets}{Total Current Liabilities}',
  quickratio: 'Quick Ratio=\\frac{Cash and Equivalents+Short Term Investments+Note Receivable+Accounts Receivable}{Total Current Liabilities}'
};

const __updatedDisplayNames = [
  'currentratio',
  'altmanzscore',
  'assetturnover',
  'bookvaluepershare',
  'capex',
  'croic',
  'debt',
  'debttoebitda',
  'debttoequity',
  'debttonopat',
  'debttototalcapital',
  'dillutedearningspershare',
  'dso',
  'dividendyield',
  'divpayoutratio',
  'ebit',
  'ebitda',
  'ebitdagrowth',
  'ebitdamargin',
  'ebitgrowth',
  'ebitmargin',
  'efftaxrate',
  'enterprisevalue',
  'enterprisevaluetoebitda',
  'enterprisevaluetoebit',
  'enterprisevaluetoocf',
  'epsgrowth',
  'fcffgrowth',
  'goodwill',
  'grossmargin',
  'interestburdenpct',
  'longtermdebt',
  'ltdebttoebitda',
  'ltdebttoequity',
  'ltdebttonopat',
  'ltdebttocap',
  'marketcap',
  'netincome',
  'netincomegrowth',
  'netinventory',
  'nopat',
  'nopatgrowth',
  'nopatmargin',
  'operatingmargin',
  'paymentofdividends',
  'pricetobook',
  'pricetoearnings',
  'pricetorevenue',
  'profitmargin',
  'quickratio',
  'repurchaseofcommonequity',
  'roe',
  'revenuegrowth',
  'roa',
  'roic',
  'tangbookvaluepershare',
  'totalrevenue'
] as const satisfies graphql.GlAccountTypeName[];



type ModifiedGLAccountType = typeof __updatedDisplayNames[number];
